import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import { ComponentErrorBoundary } from "core/components/ComponentErrorBoundary";
import { CustomLink } from "core/components/CustomLink";
import { ErrorComponent } from "core/components/ErrorComponent";
import {
  GridAreaContainer,
  type GridAreaContainerProps,
} from "core/components/GridAreaContainer";
import { StyledTabs } from "core/components/StyledTabs";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useLocale } from "core/hooks/useLocale";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { NewsWidgetTile } from "./NewsWidgetTile";

function Wrapper({
  children,
  containerProps,
}: {
  children: ReactNode;
  containerProps?: Omit<
    GridAreaContainerProps,
    "title" | "children" | "gridArea"
  >;
}) {
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:NewsWidget",
  });
  return (
    <ComponentErrorBoundary fallBackBoxProps={{ sx: { gridArea: "news" } }}>
      <GridAreaContainer
        {...containerProps}
        gridArea="news"
        title={t("gridAreaContainerTitle")}
        BottomRightSlot={
          <Button
            startIcon={<AddIcon />}
            color="primary"
            href="/news"
            LinkComponent={CustomLink}
          >
            {t("seeAllNews")}
          </Button>
        }
      >
        {children}
      </GridAreaContainer>
    </ComponentErrorBoundary>
  );
}

export function NewsWidgetV2({
  containerProps,
}: {
  containerProps?: Omit<
    GridAreaContainerProps,
    "title" | "children" | "gridArea"
  >;
}) {
  const [tabIndex, setTabIndex] = useState<number>(0);
  const locale = useLocale();
  const { data, isLoading, isError } = useCentrixApi({
    path: "/api/news-events/articles-v2/news-widget",
    parameters: {
      query: { locale, maxCategories: 4, maxArticlesPerCategory: 3 },
    },
  });

  function handleTabChange(_: any, newTabValue: number) {
    setTabIndex(newTabValue);
  }

  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:NewsWidget",
  });
  if (isError) {
    return (
      <Wrapper containerProps={containerProps}>
        <Paper sx={{ p: 3, height: "100%", minHeight: "970px" }}>
          <ErrorComponent apiError={isError} />
        </Paper>
      </Wrapper>
    );
  }

  if (!data || isLoading) {
    return (
      <Wrapper containerProps={containerProps}>
        <Box sx={{ height: "100%", minHeight: "970px" }}>
          <Skeleton variant="rounded" height="100%" />
        </Box>
      </Wrapper>
    );
  }
  const displayNoCategoriesMessage = data.totalCount === 0;
  const currentArticles = data.items?.[tabIndex]?.articles ?? [];

  const NoArticlesMessage = (
    <Typography variant="body1" color="text.secondary">
      {t("noNews")}
    </Typography>
  );

  if (data.totalCount === 0) {
    return (
      <Wrapper containerProps={containerProps}>
        <Paper
          sx={{
            p: 3,
            height: "100%",
            minHeight: "990px",
          }}
        >
          {displayNoCategoriesMessage && (
            <Typography variant="body1" color="text.secondary">
              {t("noCategories")}
            </Typography>
          )}
        </Paper>
      </Wrapper>
    );
  }
  return (
    <Wrapper containerProps={containerProps}>
      <Paper
        sx={{
          p: 3,
          height: "100%",
          minHeight: "990px",
        }}
      >
        <Stack>
          <Box>
            <StyledTabs
              sx={{ maxWidth: "100%", "& .MuiTab-root": { flex: 1 } }}
              value={tabIndex}
              onChange={handleTabChange}
              variant="scrollable"
            >
              {data.items?.map(({ name, id }, index) => (
                <Tab key={id} value={index} label={name} />
              ))}
            </StyledTabs>
            <Divider />
          </Box>
          <Stack flex={1}>
            {currentArticles.length === 0
              ? NoArticlesMessage
              : currentArticles.map((props) => (
                  <NewsWidgetTile
                    key={props.id}
                    id={props.id ?? ""}
                    title={props.title ?? ""}
                    blurb={props.blurb ?? ""}
                    publishedTime={props.publishedTime ?? ""}
                    typeId={props.typeId}
                    typeName={props.typeName ?? ""}
                    bannerBlobName={props.bannerBlobName ?? ""}
                    bannerAltText={props.bannerAltText ?? ""}
                    blurDataUrl={props.bannerBlurDataUrl ?? ""}
                    isImportant={props.isImportant}
                  />
                ))}
          </Stack>
        </Stack>
      </Paper>
    </Wrapper>
  );
}
